import { Link } from "@tanstack/react-router";
import { ShieldIcon, ArrowLeftIcon, MailIcon, ExternalLinkIcon, CheckCircleIcon } from "./LegalIcons.tsx";

export function PrivacyPolicyPage() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-app, #0f172a)",
        color: "var(--text-1, #f8fafc)",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        padding: "40px 20px 80px",
      }}
    >
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        {/* Header Bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 32,
            borderBottom: "1px solid var(--border-soft, rgba(255,255,255,0.08))",
            paddingBottom: 20,
          }}
        >
          <Link
            to="/dashboard"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "var(--text-2, #94a3b8)",
              textDecoration: "none",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <ArrowLeftIcon size={16} /> Back to SellSynq
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                color: "#fff",
                fontSize: 14,
              }}
            >
              S
            </div>
            <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: "-0.02em" }}>SellSynq</span>
          </div>
        </div>

        {/* Hero Section */}
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(99, 102, 241, 0.12)",
              color: "#818cf8",
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 14,
            }}
          >
            <ShieldIcon size={14} /> Official Application Privacy Policy
          </div>
          <h1
            style={{
              fontSize: 32,
              fontWeight: 800,
              margin: "0 0 12px",
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
            }}
          >
            SellSynq Privacy Policy
          </h1>
          <p style={{ color: "var(--text-3, #64748b)", fontSize: 13, margin: 0 }}>
            Last Updated: October 26, 2026 • Effective Date: October 26, 2026 • Domain: sellsynq.com
          </p>
        </div>

        {/* Legal Body */}
        <div
          style={{
            background: "var(--surface, rgba(30, 41, 59, 0.7))",
            border: "1px solid var(--border-soft, rgba(255,255,255,0.08))",
            borderRadius: 16,
            padding: "36px 32px",
            lineHeight: 1.7,
            fontSize: 14.5,
            color: "var(--text-2, #cbd5e1)",
          }}
        >
          {/* Section 1 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              1. Overview &amp; Introduction
            </h2>
            <p>
              Welcome to <strong>SellSynq</strong> ("SellSynq", "we", "us", or "our"), accessible at{" "}
              <a href="https://sellsynq.com" style={{ color: "#818cf8" }}>https://sellsynq.com</a> and associated
              organization subdomains. SellSynq is an enterprise AI Sales Intelligence and Live Meeting Copilot platform
              designed to assist sales professionals, account executives, and teams with real-time coaching, call
              grounding, and workflow automation.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, store, protect, and disclose information when you use
              our web application, services, and third-party integrations (including Google Calendar, Google Drive,
              Monday.com, and Microsoft SharePoint).
            </p>
          </section>

          {/* Section 2 - Google User Data */}
          <section
            style={{
              marginBottom: 32,
              background: "rgba(99, 102, 241, 0.05)",
              border: "1px solid rgba(99, 102, 241, 0.2)",
              borderRadius: 12,
              padding: 20,
            }}
          >
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#c7d2fe",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <CheckCircleIcon size={18} color="#818cf8" /> 2. Google User Data &amp; Limited Use Disclosure
            </h2>
            <p style={{ margin: "0 0 12px" }}>
              When you choose to connect your Google account with SellSynq, our application accesses specific Google user
              data solely to provide user-facing features:
            </p>
            <ul style={{ paddingLeft: 20, margin: "0 0 14px" }}>
              <li style={{ marginBottom: 6 }}>
                <strong>Google Calendar (read-only):</strong> We access your calendar event titles, meeting links, start/end
                times, and participant names to display your upcoming meetings in the SellSynq dashboard and enable 1-click
                meeting launch.
              </li>
              <li style={{ marginBottom: 6 }}>
                <strong>Google Drive (read-only):</strong> When you explicitly select or link internal documents from Google
                Drive into your company Knowledge Base, we read the document text solely to index it into your workspace's
                private Live Grounding store for sales coaching during customer calls.
              </li>
              <li>
                <strong>Basic Profile &amp; Email:</strong> Used to authenticate your account and identify your organization
                membership.
              </li>
            </ul>
            <div
              style={{
                background: "rgba(15, 23, 42, 0.6)",
                borderLeft: "3px solid #818cf8",
                padding: "10px 14px",
                borderRadius: 4,
                fontSize: 13.5,
              }}
            >
              <strong>Google API Limited Use Policy:</strong> SellSynq's use and transfer of information received from
              Google APIs to any other app will adhere to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#a5b4fc", textDecoration: "underline" }}
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </div>
            <p style={{ marginTop: 12, marginBottom: 0, fontSize: 13.5 }}>
              • We <strong>never</strong> sell your Google data to third parties.<br />
              • We <strong>never</strong> use your Google user data to serve advertisements or train generalized public AI
              models without your explicit consent.<br />
              • Human employees or contractors do not read your private Google data unless required for technical support
              explicitly requested by you or to comply with applicable law.
            </p>
          </section>

          {/* Section 3 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              3. Information We Collect
            </h2>
            <p>We collect the following categories of data when you use the SellSynq platform:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li>
                <strong>Account &amp; Profile Information:</strong> Name, work email address, company name, domain, and
                functional roles.
              </li>
              <li>
                <strong>Connected Integrations &amp; Knowledge Base:</strong> Documents, standard operating procedures
                (SOPs), playbooks, and boards imported from Google Drive, Monday.com, Microsoft SharePoint, or uploaded
                directly (PDF, DOCX, TXT).
              </li>
              <li>
                <strong>Meeting &amp; Live Session Data:</strong> Live transcripts, audio processing chunks, real-time
                hints, coaching sentiment indicators, and summary notes generated during authorized calls.
              </li>
              <li>
                <strong>Log &amp; Usage Data:</strong> IP addresses, browser types, timestamps, and interaction telemetry
                for security, rate-limiting, and error diagnostics.
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              4. How We Use Your Information
            </h2>
            <p>We use the collected information for the following business purposes:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li>To provide, operate, maintain, and improve the SellSynq service.</li>
              <li>To synchronize your upcoming sales calendar events into the coaching dashboard.</li>
              <li>To ground real-time coaching suggestions and objections handling in your company's own SOPs.</li>
              <li>To generate post-meeting action items, recaps, and CRM updates.</li>
              <li>To safeguard against unauthorized access, malicious activity, and service abuse.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              5. Data Storage, Security &amp; Retention
            </h2>
            <p>
              SellSynq employs industry-standard administrative, physical, and technical safeguards. All data in transit is
              encrypted using Transport Layer Security (TLS 1.2/1.3), and all data at rest is stored in secure, Google Cloud
              Platform enterprise infrastructure with automated AES-256 encryption.
            </p>
            <p>
              We retain customer data for as long as your workspace account remains active. When you delete a document,
              meeting, or disconnect an integration, the associated data is purged from our active databases and file search
              indexes immediately.
            </p>
          </section>

          {/* Section 6 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              6. Your Rights &amp; Revocation of Access
            </h2>
            <p>
              You maintain full ownership of your data at all times. You can disconnect your Google Calendar or Google Drive
              at any time directly from the SellSynq Dashboard or through the{" "}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#818cf8" }}
              >
                Google Security Permissions Page <ExternalLinkIcon size={12} style={{ verticalAlign: "middle" }} />
              </a>
              . Disconnecting immediately terminates token refresh access. You can also request complete account and data
              deletion by contacting our support team.
            </p>
          </section>

          {/* Section 7 */}
          <section style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              7. Contact Us
            </h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please
              reach out to us:
            </p>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 8,
                background: "var(--surface-hover, rgba(255,255,255,0.06))",
                color: "#e2e8f0",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <MailIcon size={16} color="#818cf8" /> support@sellsynq.com • moshe@echotora.com
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div
          style={{
            marginTop: 32,
            textAlign: "center",
            fontSize: 13,
            color: "var(--text-3, #64748b)",
            display: "flex",
            justifyContent: "center",
            gap: 20,
          }}
        >
          <Link to="/support" style={{ color: "var(--text-3, #64748b)", textDecoration: "none" }}>
            Terms of Service &amp; Support
          </Link>
          <span>•</span>
          <a href="https://sellsynq.com" style={{ color: "var(--text-3, #64748b)", textDecoration: "none" }}>
            SellSynq Homepage
          </a>
        </div>
      </div>
    </div>
  );
}
